import {
  blocksToMarkdown,
  type SerializableBlock,
} from './serialize-text.js';

const PAGE_CONTAINER_TYPES = new Set(['page', 'database']);

export type FlattenableBlock = {
  id: string;
  type: string;
  title?: string | null;
  content?: Record<string, unknown> | null;
  parentId?: string | null;
  position?: number;
};

/** Walk page content blocks in document order with nesting depth (mirrors agent read_page). */
export function flattenPageContentBlocks(
  pageId: string,
  rows: FlattenableBlock[],
): SerializableBlock[] {
  const byParent = new Map<string, FlattenableBlock[]>();
  for (const row of rows) {
    const key = row.parentId ?? pageId;
    const list = byParent.get(key) ?? [];
    list.push(row);
    byParent.set(key, list);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id.localeCompare(b.id));
  }

  const flattened: SerializableBlock[] = [];

  function walk(parentId: string, depth: number): void {
    const children = byParent.get(parentId) ?? [];
    for (const child of children) {
      if (PAGE_CONTAINER_TYPES.has(child.type)) {
        continue;
      }
      flattened.push({
        type: child.type,
        title: child.title,
        content: child.content,
        depth,
      });
      walk(child.id, depth + 1);
    }
  }

  walk(pageId, 0);
  return flattened;
}

export function pageDocumentToMarkdown(
  title: string,
  pageId: string,
  contentBlocks: FlattenableBlock[],
): string {
  const heading = `# ${title.trim() || 'Untitled'}`;
  const body = blocksToMarkdown(flattenPageContentBlocks(pageId, contentBlocks));
  return body ? `${heading}\n\n${body}` : heading;
}

export function pageExportFilename(title: string): string {
  const base = title.trim() || 'Untitled';
  const slug = base
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 80);
  return `${slug || 'untitled'}.md`;
}
